import { useCallback, useSyncExternalStore } from "react";
import type { ApiTag } from "@webcore/services/api/api";
import { getDataChangeVersion, subscribeToDataChangeVersions } from "@webcore/services/signalr/dataChangeVersions";

/**
 * Re-renders the caller whenever a data-change notification invalidates `tag`, returning how many
 * invalidations have been seen for it. For code that lives outside the RTK Query cache and so cannot
 * be refreshed by invalidating a tag — a custom dashboard view fetching KPI data imperatively, for
 * example — this is the signal that the underlying data moved.
 *
 * Pass `scope` (a pipeline configuration id) to observe only that configuration's changes plus
 * unscoped ones: a branch's gold run completing then moves that branch's consumers without touching
 * live-bound ones, and vice versa.
 */
export function useDataChangeVersion(tag: ApiTag, scope?: string): number {
  const getSnapshot = useCallback(() => getDataChangeVersion(tag, scope), [tag, scope]);
  return useSyncExternalStore(subscribeToDataChangeVersions, getSnapshot, getSnapshot);
}
